import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  CircularProgress,
  Box, Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";

const EditUserGroup = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { userGroupId } = useParams();
  const encodedFormId = state?.encodedFormId || "MjE=";
  const [userGroupName, setUserGroupName] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserGroup = async () => {
    try {
      console.log("userGroupId from useParams:", userGroupId); // Debug log
      console.log("Authorization token:", sessionStorage.getItem("token")); // Debug log
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/CPanel/GetUserGroup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log("GetUserGroup API response:", responseData);
        const userGroup = responseData.find(
          (item) => item.userGroupId === Number(userGroupId)
        );
        if (userGroup) {
          setUserGroupName(userGroup.userGroupName);
        } else {
          setError(`User group with ID ${userGroupId} not found`);
          setOpenErrorDialog(true);
        }
      } else {
        let errorMsg = "Failed to fetch user group data";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to view user groups";
        else if (response.status === 404)
          errorMsg = `User group with ID ${userGroupId} not found`;
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setError(errorMsg);
        setOpenErrorDialog(true);
      }
    } catch (error) {
      console.error("Error fetching user group:", error);
      setError("Network error: Unable to connect to the server");
      setOpenErrorDialog(true);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("useEffect triggered with userGroupId:", userGroupId); // Debug log
    if (!userGroupId || isNaN(parseInt(userGroupId))) {
      setError("Invalid user group ID");
      setOpenErrorDialog(true);
      setIsLoading(false);
    } else {
      fetchUserGroup();
    }
  }, [userGroupId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userGroupName.trim()) {
      setError("User Group Name is required");
      setOpenErrorDialog(true);
      return;
    }

    setIsSubmitting(true);
    setError("");
    setLoadingBarProgress(30);

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/CPanel/Id?UserGroupId=${userGroupId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            userGroupName: userGroupName.trim(),
          }),
        }
      );

      if (response.ok) {
        setOpenSuccessDialog(true);
      } else {
        let errorMsg = "Failed to update user group";
        if (response.status === 400) errorMsg = "Invalid data provided";
        else if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to update user groups";
        else if (response.status === 404)
          errorMsg = `User group with ID ${userGroupId} not found`;
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setError(errorMsg);
        setOpenErrorDialog(true);
      }
    } catch (error) {
      console.error("Error updating user group:", error);
      setError("Network error: Unable to connect to the server");
      setOpenErrorDialog(true);
    } finally {
      setLoadingBarProgress(100);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
   navigate(-1);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
    console.log("encodedFormId in handleCloseSuccessDialog:", encodedFormId); // Debug log
    navigate(`/usergroup/${encodedFormId}`);
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
    setError("");
  };

  if (isLoading) {
    return (
      <div className="container mx-auto mt-4">
        <LoadingBar
          progress={loadingBarProgress}
          color="rgb(152, 106, 182)"
          height={5}
        />
        <Typography>Loading...</Typography>
      </div>
    );
  }

  return (
    <div className="container mx-auto mt-4">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <Box
          sx={{
            backgroundColor: '#0B1F40', // dark navy blue
            padding: '12px 24px',
            boxShadow: 3,
            borderRadius: '4px 4px 0 0',
            margin: '2px auto',
           maxWidth: "900px",
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff', textAlign:'center', fontWeight: 'bold' }}>
        Edit User Group
      </Typography> </Box>

      <Paper
        elevation={3}
        style={{
          padding: 16,
          marginTop: 2,
          maxWidth: "900px",
          margin: "16px auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom style={{color:'#127498ff'}}>
            Edit User Group Details
          </Typography>
          <TextField
            label="User Group Name"
            variant="outlined"
            fullWidth
            value={userGroupName}
            onChange={(e) => setUserGroupName(e.target.value)}
            required
            sx={{ mb: 3 }}
            error={!!error && !userGroupName.trim()}
            helperText={
              !!error && !userGroupName.trim()
                ? "User Group Name is required"
                : ""
            }
          />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              style={{ width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'}}
            >
              SAVE
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{ width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'}}
            >
              CANCEL
            </Button>
          </Box>
        </form>
      </Paper>

    <Modal
  open={openSuccessDialog}
  onClose={() => setOpenSuccessDialog(false)}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
        User Group Updated ✅
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="success-modal-description" sx={{ mb: 2 }}>
        The User Group has been updated successfully.
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => {
            setOpenSuccessDialog(false);
            navigate(-1); // go back after closing modal
          }}
          variant="contained"
          color="primary"
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>


      <Dialog
        open={openErrorDialog}
        onClose={handleCloseErrorDialog}
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title">Error</DialogTitle>
        <DialogContent>
          <DialogContentText>{error}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditUserGroup;
